import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Typography,
  Alert,
  CircularProgress,
  Box,
  Divider,
  Collapse
} from '@mui/material';

// API Configuration
const API_BASE_URL = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' 
  ? 'https://barebiologics-api-bdgjaefwc4h7d9gu.eastus2-01.azurewebsites.net/api'
  : 'http://localhost:8080/api');

const ReceiveDonorDialog = ({ open, onClose, onSuccess }) => {
  // Form state
  const [mareName, setMareName] = useState('');
  const [nextDonorId, setNextDonorId] = useState('');
  const [customDonorId, setCustomDonorId] = useState('');
  const [donorIdError, setDonorIdError] = useState('');
  const [tissueTypes, setTissueTypes] = useState({ AM: false, UC: false });
  const [dateReceived, setDateReceived] = useState(new Date().toISOString().split('T')[0]);
  const [shipperTemp, setShipperTemp] = useState('');
  const [wetIcePresent, setWetIcePresent] = useState(false);
  const [contaminationEvidence, setContaminationEvidence] = useState(false);
  const [contaminationNotes, setContaminationNotes] = useState('');
  const [disposition, setDisposition] = useState('PROCESSING');
  const [dispositionNotes, setDispositionNotes] = useState('');
  const [tissueLabeledConfirmed, setTissueLabeledConfirmed] = useState(false);
  const [notes, setNotes] = useState('');

  // UI state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Fetch next donor ID when dialog opens
  useEffect(() => {
    if (open) {
      setCustomDonorId(''); // Clear custom ID when dialog opens
      setDonorIdError('');
      fetchNextDonorId();
    }
  }, [open]);

  const fetchNextDonorId = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_BASE_URL}/donors/next-id`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        const suggestedId = data.data?.nextDonorId || 'Will be auto-generated';
        setNextDonorId(suggestedId);
      }
    } catch (err) {
      console.error('Error fetching next donor ID:', err);
      setNextDonorId('Will be auto-generated');
    }
  };

  const handleTissueChange = (type, checked) => {
    setTissueTypes(prev => ({ ...prev, [type]: checked }));
  };

  const validateDonorId = (id) => {
    if (!id || id.trim() === '') {
      setDonorIdError('');
      return true; // Empty is OK, will use auto-generated
    }
    
    const idRegex = /^EPL(\d{2})-(\d{3})$/;
    if (!idRegex.test(id.trim())) {
      setDonorIdError('Invalid format. Expected: EPL26-001 (EPL + 2-digit year + dash + 3-digit number)');
      return false;
    }
    
    setDonorIdError('');
    return true;
  };

  const handleDonorIdChange = (e) => {
    const value = e.target.value;
    setCustomDonorId(value);
    validateDonorId(value);
  };

  const isFormValid = 
    mareName.trim() !== '' &&
    (tissueTypes.AM || tissueTypes.UC) &&
    dateReceived !== '' &&
    disposition !== '' &&
    tissueLabeledConfirmed === true;

  const resetForm = () => {
    setMareName('');
    setTissueTypes({ AM: false, UC: false });
    setDateReceived(new Date().toISOString().split('T')[0]);
    setShipperTemp('');
    setWetIcePresent(false);
    setContaminationEvidence(false);
    setContaminationNotes('');
    setDisposition('PROCESSING');
    setDispositionNotes('');
    setTissueLabeledConfirmed(false);
    setNotes('');
    setCustomDonorId('');
    setDonorIdError('');
    setError('');
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleSubmit = async () => {
    if (!isFormValid) {
      setError('Please fill in all required fields');
      return;
    }

    // Validate donor ID if custom one is provided
    if (customDonorId.trim() && !validateDonorId(customDonorId)) {
      setError('Please fix the donor ID format');
      return;
    }

    try {
      setLoading(true);
      setError('');

      const token = localStorage.getItem('authToken');
      const selectedTissues = [];
      if (tissueTypes.AM) selectedTissues.push('AM');
      if (tissueTypes.UC) selectedTissues.push('UC');

      const payload = {
        mareName: mareName.trim(),
        tissueTypes: selectedTissues,
        dateReceived,
        internalShipperTemp: shipperTemp ? parseFloat(shipperTemp) : null,
        wetIcePresent,
        contaminationEvidence,
        contaminationNotes: contaminationEvidence ? contaminationNotes : null,
        disposition,
        dispositionNotes: dispositionNotes || null,
        tissueLabeledConfirmed,
        notes: notes || null,
        customDonorId: customDonorId.trim() || null
      };

      const response = await fetch(`${API_BASE_URL}/donors`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to receive donor');
      }

      resetForm();
      onSuccess(data.data);
    } catch (err) {
      console.error('Error receiving donor:', err);
      setError(err.message || 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Typography variant="h5" fontWeight="bold">Receive New Donor</Typography>
        <Typography variant="body2" color="text.secondary">
          Record donor tissue receipt information
        </Typography>
      </DialogTitle>
      
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Donor Information */}
        <Typography variant="subtitle1" fontWeight="bold" sx={{ mt: 2, mb: 1, color: 'primary.main' }}>
          Donor Information
        </Typography>
        
        <TextField
          label="Mare Name *"
          value={mareName}
          onChange={(e) => setMareName(e.target.value)}
          fullWidth
          required
          sx={{ mb: 2 }}
          placeholder="e.g., Thunder, Belle"
        />
        
        <TextField
          label="EPL Donor ID"
          value={customDonorId}
          onChange={handleDonorIdChange}
          fullWidth
          placeholder={nextDonorId || 'EPL26-001'}
          error={!!donorIdError}
          helperText={donorIdError || `Suggested: ${nextDonorId || 'Will be auto-generated'} (Format: EPL{YY}-{NNN})`}
          sx={{ mb: 2 }}
          inputProps={{
            pattern: 'EPL\\d{2}-\\d{3}',
            style: { fontFamily: 'monospace' }
          }}
        />

        {/* Tissues Received */}
        <Typography variant="subtitle1" fontWeight="bold" sx={{ mt: 2, mb: 1, color: 'primary.main' }}>
          Tissues Received *
        </Typography>
        
        <FormGroup row sx={{ mb: 1 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={tissueTypes.AM}
                onChange={(e) => handleTissueChange('AM', e.target.checked)}
              />
            }
            label="AM (Amniotic Membrane)"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={tissueTypes.UC}
                onChange={(e) => handleTissueChange('UC', e.target.checked)}
              />
            }
            label="UC (Umbilical Cord)"
          />
        </FormGroup>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          Select one or both tissue types.
        </Typography>
        
        <Collapse in={tissueTypes.AM && tissueTypes.UC}>
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2">
              <strong>Note:</strong> Selecting both AM and UC will create <strong>two separate donor records</strong>{' '}
              (e.g., EPL26-001-AM and EPL26-001-UC) that share the same recovery documentation. 
              Each tissue type will be tracked independently through the workflow after receiving.
            </Typography>
          </Alert>
        </Collapse>

        {/* Date Received */}
        <TextField
          label="Date Received *"
          type="date"
          value={dateReceived}
          onChange={(e) => setDateReceived(e.target.value)}
          fullWidth
          InputLabelProps={{ shrink: true }}
          sx={{ mb: 2 }}
        />

        <Divider sx={{ my: 2 }} />

        {/* Shipping Condition */}
        <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1, color: 'primary.main' }}>
          Shipping Condition
        </Typography>
        
        <TextField
          label="Internal Shipper Temperature (°C)"
          type="number"
          value={shipperTemp}
          onChange={(e) => setShipperTemp(e.target.value)}
          fullWidth
          inputProps={{ step: 0.1 }}
          sx={{ mb: 2 }}
          helperText="Record the temperature inside the shipper upon receipt"
        />
        
        <FormGroup sx={{ mb: 2 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={wetIcePresent}
                onChange={(e) => setWetIcePresent(e.target.checked)}
              />
            }
            label={
              <Box>
                <Typography component="span">Wet Ice Present?</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                  (check box if Yes)
                </Typography>
              </Box>
            }
          />
          
          <FormControlLabel
            control={
              <Checkbox
                checked={contaminationEvidence}
                onChange={(e) => setContaminationEvidence(e.target.checked)}
                color="error"
              />
            }
            label={
              <Box>
                <Typography component="span">Any Evidence of Contamination/Cross-Contamination?</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                  (check box if Yes)
                </Typography>
              </Box>
            }
          />
        </FormGroup>
        
        {contaminationEvidence && (
          <TextField
            label="Contamination Notes"
            multiline
            rows={2}
            value={contaminationNotes}
            onChange={(e) => setContaminationNotes(e.target.value)}
            fullWidth
            sx={{ mb: 2 }}
            error
            helperText="Document any contamination concerns"
          />
        )}

        <Divider sx={{ my: 2 }} />

        {/* Tissue Disposition */}
        <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1, color: 'primary.main' }}>
          Tissue Disposition *
        </Typography>
        
        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel>Disposition</InputLabel>
          <Select
            value={disposition}
            onChange={(e) => setDisposition(e.target.value)}
            label="Disposition"
          >
            <MenuItem value="PROCESSING">Processing</MenuItem>
            <MenuItem value="R_AND_D">R&D</MenuItem>
            <MenuItem value="DISCARD">Discard</MenuItem>
          </Select>
        </FormControl>

        {disposition === 'DISCARD' && (
          <TextField
            label="Reason for Discard"
            multiline
            rows={2}
            value={dispositionNotes}
            onChange={(e) => setDispositionNotes(e.target.value)}
            fullWidth
            sx={{ mb: 2 }}
          />
        )}

        <Divider sx={{ my: 2 }} />

        {/* Labeling Confirmation */}
        <Box sx={{ bgcolor: 'action.hover', p: 2, borderRadius: 1, mb: 2 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={tissueLabeledConfirmed}
                onChange={(e) => setTissueLabeledConfirmed(e.target.checked)}
                color="success"
              />
            }
            label={
              <Typography fontWeight="medium">
                I confirm all donor tissue has been labeled with the assigned EPL Donor ID *
              </Typography>
            }
          />
        </Box>

        {/* Notes */}
        <TextField
          label="Additional Notes (Optional)"
          multiline
          rows={3}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          fullWidth
          sx={{ mt: 2 }}
        />
      </DialogContent>
      
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained"
          disabled={!isFormValid || loading}
          startIcon={loading ? <CircularProgress size={20} /> : null}
        >
          {loading ? 'Saving...' : 'Receive Donor'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ReceiveDonorDialog;

